/**
 * Timezone Selector Component
 * Reusable dropdown for selecting US timezones (plus common international)
 * Defaults to America/New_York (Eastern) for USLS users
 */

import React from "react";

export interface TimezoneOption {
  value: string; // IANA timezone name
  label: string;
  abbr: string; // Current abbreviation (e.g., CST or CDT)
}

/**
 * Get the current UTC offset string for a timezone (e.g., "-06:00")
 * DST-aware: uses the provided reference date to determine the correct offset
 */
export function getTimezoneOffset(
  timezone: string,
  referenceDate?: string,
): string {
  // referenceDate may be date-only ("2026-02-15") or datetime ("2026-02-15T14:30")
  const date = referenceDate
    ? new Date(
        referenceDate.includes("T")
          ? referenceDate
          : `${referenceDate}T12:00:00`,
      )
    : new Date();
  if (isNaN(date.getTime())) {
    return "+00:00";
  }
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
  });
  const parts = formatter.formatToParts(date);
  const offsetPart = parts.find((p) => p.type === "timeZoneName");

  if (!offsetPart) {
    return "+00:00";
  }

  // offsetPart.value is like "GMT-6" or "GMT+5:30" or "GMT"
  const raw = offsetPart.value.replace("GMT", "");
  if (!raw || raw === "") {
    return "+00:00";
  }

  // Parse the offset
  const match = raw.match(/^([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!match) {
    return "+00:00";
  }

  const sign = match[1];
  const hours = match[2].padStart(2, "0");
  const minutes = match[3] || "00";
  return `${sign}${hours}:${minutes}`;
}

/**
 * Get the current timezone abbreviation (e.g., "CST", "CDT", "EST")
 */
function getTimezoneAbbr(timezone: string): string {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "short",
  });
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find((p) => p.type === "timeZoneName");
  return tzPart?.value || "";
}

export const US_TIMEZONES: TimezoneOption[] = [
  {
    value: "America/New_York",
    label: "Eastern",
    abbr: getTimezoneAbbr("America/New_York"),
  },
  {
    value: "America/Chicago",
    label: "Central",
    abbr: getTimezoneAbbr("America/Chicago"),
  },
  {
    value: "America/Denver",
    label: "Mountain",
    abbr: getTimezoneAbbr("America/Denver"),
  },
  {
    value: "America/Phoenix",
    label: "Arizona (no DST)",
    abbr: getTimezoneAbbr("America/Phoenix"),
  },
  {
    value: "America/Los_Angeles",
    label: "Pacific",
    abbr: getTimezoneAbbr("America/Los_Angeles"),
  },
  {
    value: "America/Anchorage",
    label: "Alaska",
    abbr: getTimezoneAbbr("America/Anchorage"),
  },
  {
    value: "Pacific/Honolulu",
    label: "Hawaii (no DST)",
    abbr: getTimezoneAbbr("Pacific/Honolulu"),
  },
];

export const DEFAULT_TIMEZONE = "America/New_York";

interface TimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  /** EVT-08: where an auto-picked zone came from, e.g. "(from ZIP 79901)". */
  hint?: string;
  /** EVT-08b: shown instead when nothing was auto-selected, e.g. "Enter the location's postal code ...". */
  note?: string;
}

const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value,
  onChange,
  required = false,
  disabled = false,
  label = "Time Zone",
  hint,
  note,
}) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <option value="">Select timezone...</option>
        {US_TIMEZONES.map((tz) => (
          <option key={tz.value} value={tz.value}>
            {tz.label} ({tz.abbr})
          </option>
        ))}
      </select>
      {hint ? (
        <p className="mt-1 text-xs text-gray-500" data-testid="timezone-hint">
          Auto-selected {hint}. You can change it.
        </p>
      ) : note ? (
        <p className="mt-1 text-xs text-amber-700" data-testid="timezone-note">
          {note}
        </p>
      ) : null}
    </div>
  );
};

export default TimezoneSelector;
